/* Admin: Public marketing — manage which cars are shown on mahasa.dk + photos, prices, description. */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Globe, Tag, X, Check, Loader2, Handshake, Trash2 } from "lucide-react";
import PhotoUploader from "../components/PhotoUploader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Mikro", "Hatchback", "SUV", "Stationcar", "Sedan", "Minibus", "Crossover", "Cabriolet", "Coupe", "Varevogn"];
const FUELS = ["El", "Benzin", "Diesel", "Hybrid"];
const TRANSMISSIONS = ["Automatgear", "Manuelt"];

const FILTERS = [
  { key: "all", label: "Alle" },
  { key: "gomore", label: "GoMore" },
  { key: "carbuddi", label: "Carbuddi" },
  { key: "lager", label: "Lager" },
  { key: "formidling", label: "Formidling" },
];

function authHdr() {
  const t = localStorage.getItem("mahasa_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function carCategory(c) {
  if (c.status === "formidling") return "formidling";
  if (c.status === "lager") return "lager";
  return c.platform === "carbuddi" ? "carbuddi" : "gomore";
}

export default function PublicMarketing() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [showAddDialog, setShowAddDialog] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/cars`, { headers: authHdr(), params: { status_filter: "all" } });
      setCars(r.data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const base = cars.filter((c) => c.status !== "sold");
    return {
      all: base.length,
      gomore: base.filter((c) => carCategory(c) === "gomore").length,
      carbuddi: base.filter((c) => carCategory(c) === "carbuddi").length,
      lager: base.filter((c) => carCategory(c) === "lager").length,
      formidling: base.filter((c) => carCategory(c) === "formidling").length,
    };
  }, [cars]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let base = cars.filter((c) => c.status !== "sold");
    if (filter !== "all") base = base.filter((c) => carCategory(c) === filter);
    if (!q) return base;
    return base.filter((c) =>
      (c.license_plate || "").toLowerCase().includes(q) ||
      (c.make || "").toLowerCase().includes(q) ||
      (c.model || "").toLowerCase().includes(q)
    );
  }, [cars, search, filter]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Globe size={22} className="text-amber-500"/>Hjemmeside</h1>
          <p className="text-zinc-400 text-sm mt-1">Styr hvilke biler der vises offentligt på mahasa.dk — upload billeder, sæt priser og beskrivelser.</p>
        </div>
        <button
          onClick={() => setShowAddDialog(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-zinc-900 font-medium rounded-lg hover:bg-amber-400 text-sm shrink-0"
          data-testid="add-consignment-btn"
        >
          <Handshake size={14}/> Ny formidlings-bil
        </button>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2" data-testid="marketing-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              filter === f.key
                ? "bg-amber-500 text-zinc-900 border-amber-500"
                : "bg-zinc-900 text-zinc-300 border-zinc-700 hover:border-zinc-500"
            }`}
            data-testid={`filter-${f.key}`}
          >
            {f.label}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${filter === f.key ? "bg-zinc-900/30" : "bg-zinc-800"}`}>
              {counts[f.key] ?? 0}
            </span>
          </button>
        ))}
      </div>

      <input
        className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-2 text-sm"
        placeholder="Søg på plade, mærke eller model…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        data-testid="public-search"
      />

      {loading && <div className="text-zinc-500 text-sm">Indlæser…</div>}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 text-zinc-500 text-sm border border-dashed border-zinc-800 rounded-xl" data-testid="empty-state">
          {filter === "formidling"
            ? "Ingen formidlings-biler endnu. Tryk på 'Ny formidlings-bil' for at oprette en."
            : "Ingen biler matcher dette filter."}
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((c) => (
          <CarRow key={c.id} car={c} open={openId === c.id} onToggle={() => setOpenId(openId === c.id ? null : c.id)} onSaved={load}/>
        ))}
      </div>

      {showAddDialog && (
        <AddConsignmentDialog
          onClose={() => setShowAddDialog(false)}
          onCreated={(carId) => { setShowAddDialog(false); load().then(() => setOpenId(carId)); }}
        />
      )}
    </div>
  );
}

function AddConsignmentDialog({ onClose, onCreated }) {
  const [plate, setPlate] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!plate.trim()) { toast.error("Indtast registreringsnummer"); return; }
    setBusy(true);
    try {
      const body = {
        license_plate: plate.trim().toUpperCase().replace(/\s+/g, ""),
        make: make.trim(),
        model: model.trim(),
        year: year ? parseInt(year, 10) : null,
        status: "formidling",
        platform: "gomore",  // ignored for formidling but required by enum
        is_public_for_sale: true,
      };
      const r = await axios.post(`${API}/cars`, body, { headers: authHdr() });
      toast.success("Formidlings-bil oprettet");
      onCreated(r.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="add-consignment-dialog">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-amber-500/15 text-amber-400 flex items-center justify-center shrink-0">
            <Handshake size={18}/>
          </div>
          <div>
            <h2 className="text-lg font-semibold">Ny formidlings-bil</h2>
            <p className="text-xs text-zinc-500 mt-1">Bil til formidlings-salg — ejes ikke af firmaet og påvirker ikke dashboard, omsætning eller omkostninger.</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="overline">Registreringsnummer *</label>
            <input
              className="ui-input mt-1.5 font-mono uppercase"
              value={plate}
              onChange={(e) => setPlate(e.target.value)}
              placeholder="AB12345"
              required
              autoFocus
              data-testid="consignment-plate"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline">Mærke</label>
              <input className="ui-input mt-1.5" value={make} onChange={(e) => setMake(e.target.value)} placeholder="Toyota" data-testid="consignment-make"/>
            </div>
            <div>
              <label className="overline">Model</label>
              <input className="ui-input mt-1.5" value={model} onChange={(e) => setModel(e.target.value)} placeholder="Aygo" data-testid="consignment-model"/>
            </div>
          </div>
          <div>
            <label className="overline">Årgang</label>
            <input type="number" className="ui-input mt-1.5" value={year} onChange={(e) => setYear(e.target.value)} placeholder="2020" data-testid="consignment-year"/>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-zinc-400 hover:text-white" data-testid="consignment-cancel">Annullér</button>
            <button type="submit" disabled={busy} className="px-4 py-2 bg-amber-500 text-zinc-900 font-medium rounded-lg hover:bg-amber-400 text-sm disabled:opacity-50" data-testid="consignment-submit">
              {busy ? "Opretter…" : "Opret"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CarRow({ car, open, onToggle, onSaved }) {
  const [form, setForm] = useState(initForm(car));
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const firstRenderRef = useRef(true);
  const timerRef = useRef(null);

  function initForm(c) {
    return {
      is_public_for_rent: !!c.is_public_for_rent,
      is_public_for_sale: !!c.is_public_for_sale,
      is_rented_out: !!c.is_rented_out,
      rental_return_date: c.rental_return_date || "",
      sub_price_1m: c.sub_price_1m ?? "",
      sub_price_3m: c.sub_price_3m ?? "",
      sub_price_6m: c.sub_price_6m ?? "",
      sub_price_12m: c.sub_price_12m ?? "",
      excess_km_price: c.excess_km_price ?? 2,
      sale_price: c.sale_price ?? "",
      sale_type: c.sale_type || "",
      current_km: c.current_km ?? "",
      first_registration_date: c.first_registration_date || "",
      vin: c.vin || "",
      category: c.category || "",
      fuel_type: c.fuel_type || "",
      transmission: c.transmission || "",
      seats: c.seats ?? "",
      doors: c.doors ?? "",
      public_description: c.public_description || "",
      public_features: c.public_features || [],
      public_photos: c.public_photos || [],
      public_location: c.public_location || "",
      gomore_link: c.gomore_link || "",
      featureInput: "",
    };
  }

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function addFeature() {
    const v = form.featureInput.trim();
    if (!v) return;
    set("public_features", [...form.public_features, v]);
    set("featureInput", "");
  }

  function removeFeature(i) {
    set("public_features", form.public_features.filter((_, idx) => idx !== i));
  }

  // Build the payload that gets sent to the API (excludes UI-only fields like featureInput)
  const payload = useMemo(() => ({
    is_public_for_rent: form.is_public_for_rent,
    is_public_for_sale: form.is_public_for_sale,
    is_rented_out: form.is_rented_out,
    rental_return_date: form.rental_return_date || null,
    sub_price_1m: numOrNull(form.sub_price_1m),
    sub_price_3m: numOrNull(form.sub_price_3m),
    sub_price_6m: numOrNull(form.sub_price_6m),
    sub_price_12m: numOrNull(form.sub_price_12m),
    excess_km_price: numOrNull(form.excess_km_price),
    sale_price: numOrNull(form.sale_price),
    sale_type: form.sale_type || null,
    current_km: intOrNull(form.current_km),
    first_registration_date: form.first_registration_date || null,
    vin: (form.vin || "").trim().toUpperCase() || null,
    category: form.category || null,
    fuel_type: form.fuel_type || null,
    transmission: form.transmission || null,
    seats: intOrNull(form.seats),
    doors: intOrNull(form.doors),
    public_description: form.public_description,
    public_features: form.public_features,
    public_photos: form.public_photos,
    public_location: form.public_location || null,
    gomore_link: (form.gomore_link || "").trim() || null,
  }), [form.is_public_for_rent, form.is_public_for_sale, form.is_rented_out, form.rental_return_date, form.sub_price_1m, form.sub_price_3m, form.sub_price_6m, form.sub_price_12m, form.excess_km_price, form.sale_price, form.sale_type, form.current_km, form.first_registration_date, form.vin, form.category, form.fuel_type, form.transmission, form.seats, form.doors, form.public_description, form.public_features, form.public_photos, form.public_location, form.gomore_link]);

  const payloadKey = JSON.stringify(payload);

  // Auto-save with debounce whenever the payload changes
  useEffect(() => {
    if (firstRenderRef.current) { firstRenderRef.current = false; return; }
    if (timerRef.current) clearTimeout(timerRef.current);
    setStatus("saving");
    timerRef.current = setTimeout(async () => {
      try {
        await axios.patch(`${API}/cars/${car.id}`, payload, { headers: authHdr() });
        setStatus("saved");
        onSaved?.();
        setTimeout(() => setStatus((s) => (s === "saved" ? "idle" : s)), 1500);
      } catch (e) {
        setStatus("error");
        toast.error("Kunne ikke gemme: " + (e.response?.data?.detail || e.message));
      }
    }, 600);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey]);

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid={`marketing-row-${car.license_plate}`}>
      <button onClick={onToggle} className="w-full px-4 py-3 flex items-center justify-between hover:bg-zinc-800/40 transition-colors text-left gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="font-mono text-amber-500 text-sm">{car.license_plate}</div>
          <div className="text-zinc-300 truncate">{car.make} {car.model}</div>
          <div className="text-zinc-500 text-xs">{car.year || "—"}</div>
          {car.status === "formidling" && (
            <span className="text-[10px] px-2 py-0.5 bg-purple-500/15 text-purple-300 rounded-full uppercase tracking-wider shrink-0">Formidling</span>
          )}
          {car.status === "lager" && (
            <span className="text-[10px] px-2 py-0.5 bg-zinc-700/50 text-zinc-300 rounded-full uppercase tracking-wider shrink-0">Lager</span>
          )}
          {car.status === "active" && car.platform === "carbuddi" && (
            <span className="text-[10px] px-2 py-0.5 bg-blue-500/15 text-blue-300 rounded-full uppercase tracking-wider shrink-0">Carbuddi</span>
          )}
          {car.status === "active" && car.platform !== "carbuddi" && (
            <span className="text-[10px] px-2 py-0.5 bg-amber-500/15 text-amber-400 rounded-full uppercase tracking-wider shrink-0">GoMore</span>
          )}
        </div>
        {/* Quick-glance status indicators */}
        <div className="hidden md:flex items-center gap-2 text-[11px] shrink-0">
          {car.is_public_for_rent && (car.platform || "gomore") === "gomore" ? (
            car.gomore_link ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300" title={car.gomore_link} data-testid={`row-gomore-ok-${car.license_plate}`}>
                <Check size={11}/> GoMore link
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300" data-testid={`row-gomore-missing-${car.license_plate}`}>
                ⚠ Mangler GoMore link
              </span>
            )
          ) : null}
          {car.is_public_for_rent && (
            car.public_location ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-700/40 text-zinc-300" data-testid={`row-location-${car.license_plate}`}>
                📍 {car.public_location}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400/70" data-testid={`row-location-missing-${car.license_plate}`}>
                Ingen lokation
              </span>
            )
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {car.is_public_for_rent && <span className="text-[10px] px-2 py-0.5 bg-emerald-500/15 text-emerald-400 rounded-full">UDLEJNING</span>}
          {car.is_public_for_sale && <span className="text-[10px] px-2 py-0.5 bg-amber-500/15 text-amber-400 rounded-full">SALG</span>}
          {!car.is_public_for_rent && !car.is_public_for_sale && <span className="text-[10px] px-2 py-0.5 bg-zinc-700/40 text-zinc-500 rounded-full">SKJULT</span>}
        </div>
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-6 overflow-y-auto"
          onClick={onToggle}
          data-testid={`marketing-modal-${car.license_plate}`}
        >
          <div
            className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-4xl max-h-[95vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="sticky top-0 bg-zinc-900 z-10 border-b border-zinc-800 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="font-mono text-amber-500 text-sm">{car.license_plate}</div>
                <div className="text-zinc-200 truncate font-medium">{car.make} {car.model}</div>
                <SaveStatus status={status}/>
              </div>
              <button
                type="button"
                onClick={onToggle}
                className="w-8 h-8 rounded-full hover:bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white shrink-0"
                aria-label="Luk"
                data-testid={`modal-close-${car.license_plate}`}
              >
                <X size={16}/>
              </button>
            </div>
            <div className="p-5 space-y-5">
              {/* Toggles */}
              <div className="flex flex-wrap items-center gap-3">
                <Toggle label="Vis under Biludlejning" checked={form.is_public_for_rent} onChange={(v) => set("is_public_for_rent", v)} testid={`tgl-rent-${car.license_plate}`}/>
                <Toggle label="Vis under Biler til salg" checked={form.is_public_for_sale} onChange={(v) => set("is_public_for_sale", v)} testid={`tgl-sale-${car.license_plate}`}/>
              </div>

          {/* Photos */}
          <div>
            <Label>Billeder (første er primær)</Label>
            <PhotoUploader photos={form.public_photos} onChange={(v) => set("public_photos", v)} folder={`cars/${car.license_plate}`} compact/>
          </div>

          {/* Bilabonnement — månedspriser pr. binding */}
          {form.is_public_for_rent && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Bilabonnement · pris pr. md. (DKK)</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="1 måned">
                  <NumInput value={form.sub_price_1m} onChange={(v) => set("sub_price_1m", v)} testid="sub-price-1m"/>
                </Field>
                <Field label="3 måneder">
                  <NumInput value={form.sub_price_3m} onChange={(v) => set("sub_price_3m", v)} testid="sub-price-3m"/>
                </Field>
                <Field label="6 måneder">
                  <NumInput value={form.sub_price_6m} onChange={(v) => set("sub_price_6m", v)} testid="sub-price-6m"/>
                </Field>
                <Field label="12 måneder">
                  <NumInput value={form.sub_price_12m} onChange={(v) => set("sub_price_12m", v)} testid="sub-price-12m"/>
                </Field>
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                Pris pr. måned for hver bindingsperiode (lavere pris ved længere binding). Lader du alle felter stå tomme, vises bilen ikke under <strong>/biludlejning</strong>.
              </p>
              <div className="mt-4 pt-4 border-t border-zinc-700 grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                <Field label="Pris pr. overkørt km (DKK)">
                  <NumInput
                    value={form.excess_km_price}
                    onChange={(v) => set("excess_km_price", v)}
                    placeholder="2,00"
                    testid={`excess-km-price-${car.license_plate}`}
                  />
                </Field>
                <p className="text-xs text-zinc-500 sm:pb-2">
                  Standard er <strong>2,00 kr./km</strong>. Vises til kunden ved booking, og opkræves ved returnering hvis kilometergrænsen er overskredet.
                </p>
              </div>
            </div>
          )}

          {/* Udlejet-status — kun for Carbuddi-biler (vi styrer udlejningen selv) */}
          {form.is_public_for_rent && car.platform === "carbuddi" && (
            <div className="border border-zinc-700 p-4 bg-[#1A1410]/30">
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  id={`rented-${car.id}`}
                  checked={!!form.is_rented_out}
                  onChange={(e) => set("is_rented_out", e.target.checked)}
                  className="w-4 h-4 accent-[#C8A661]"
                  data-testid={`toggle-rented-${car.license_plate}`}
                />
                <label htmlFor={`rented-${car.id}`} className="cursor-pointer">
                  <div className="font-medium text-sm text-zinc-200">Markér som udlejet</div>
                  <div className="text-xs text-zinc-500">Bilen placeres bagest i listen på hjemmesiden og får et "UDLEJET"-badge.</div>
                </label>
              </div>
              {form.is_rented_out && (
                <Field label="Forventet retur-dato">
                  <input
                    type="date"
                    value={form.rental_return_date}
                    onChange={(e) => set("rental_return_date", e.target.value)}
                    className="ui-input"
                    data-testid={`return-date-${car.license_plate}`}
                  />
                </Field>
              )}
            </div>
          )}

          {/* Lokation + GoMore link — Lokation altid; GoMore-link kun ved GoMore-platform (manglende platform = standard "gomore") */}
          {form.is_public_for_rent && (() => {
            const isGoMore = (car.platform || "gomore") === "gomore";
            return (
            <div className={`border p-4 space-y-3 ${isGoMore ? "border-emerald-700/40 bg-emerald-900/10" : "border-zinc-700 bg-zinc-800/30"}`}>
              {isGoMore && (
                <div className="flex items-center gap-2 text-xs text-emerald-300">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                  <span><strong>GoMore-bil:</strong> Udlejes via GoMore — kunden sendes til linket nedenfor når der trykkes "Book".</span>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Lokation (vises på bilkortet)">
                  <input
                    type="text"
                    value={form.public_location || ""}
                    onChange={(e) => set("public_location", e.target.value)}
                    placeholder="fx København NV"
                    className="ui-input"
                    data-testid={`public-location-${car.license_plate}`}
                  />
                </Field>
                {isGoMore && (
                  <Field label="GoMore link (åbnes ved Book)">
                    <input
                      type="url"
                      value={form.gomore_link || ""}
                      onChange={(e) => set("gomore_link", e.target.value)}
                      placeholder="https://gomore.dk/cars/..."
                      className="ui-input"
                      data-testid={`gomore-link-${car.license_plate}`}
                    />
                  </Field>
                )}
              </div>
            </div>
            );
          })()}

          {/* Sale price */}
          {form.is_public_for_sale && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Salgspris (DKK)">
                <NumInput value={form.sale_price} onChange={(v) => set("sale_price", v)} testid="sale-price"/>
              </Field>
              <Field label="Salgs-type (valgfri)">
                <select
                  className="ui-input"
                  value={form.sale_type || ""}
                  onChange={(e) => set("sale_type", e.target.value)}
                  data-testid="sale-type"
                >
                  <option value="">Intet valg</option>
                  <option value="engros">Engros</option>
                  <option value="formidling">Formidling</option>
                </select>
              </Field>
              <div className="flex items-end">
                <p className="text-xs text-zinc-500 pb-2.5">
                  Salgs-type vises som badge på det offentlige bilkort — vælges "Intet valg" vises intet.
                </p>
              </div>
            </div>
          )}

          {/* Bil-info (km, 1. registrering, stelnummer) */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Bil-info</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Kilometer">
                <NumInput value={form.current_km} onChange={(v) => set("current_km", v)} testid="km-input"/>
              </Field>
              <Field label="1. registrering">
                <input
                  type="date"
                  className="ui-input"
                  value={form.first_registration_date || ""}
                  onChange={(e) => set("first_registration_date", e.target.value)}
                  data-testid="firstreg-input"
                />
              </Field>
              <Field label="Stelnummer (VIN)">
                <input
                  type="text"
                  className="ui-input font-mono uppercase"
                  value={form.vin || ""}
                  onChange={(e) => set("vin", e.target.value)}
                  placeholder="17 tegn"
                  maxLength={17}
                  data-testid="vin-input"
                />
              </Field>
            </div>
            <p className="text-[11px] text-zinc-600 mt-1.5">
              Kilometer vises på bilkort under "Til salg". Stelnummer vises kun på til-salg-detaljesiden.
            </p>
          </div>

          {/* Specs */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Field label="Kategori">
              <select className="ui-input" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="sel-category">
                <option value="">—</option>
                {CATEGORIES.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Brændstof">
              <select className="ui-input" value={form.fuel_type} onChange={(e) => set("fuel_type", e.target.value)} data-testid="sel-fuel">
                <option value="">—</option>
                {FUELS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Gearkasse">
              <select className="ui-input" value={form.transmission} onChange={(e) => set("transmission", e.target.value)} data-testid="sel-trans">
                <option value="">—</option>
                {TRANSMISSIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Sæder"><NumInput value={form.seats} onChange={(v) => set("seats", v)} testid="sel-seats"/></Field>
            <Field label="Døre"><NumInput value={form.doors} onChange={(v) => set("doors", v)} testid="sel-doors"/></Field>
          </div>

          {/* Description */}
          <Field label="Beskrivelse">
            <textarea className="ui-input min-h-[100px]" value={form.public_description} onChange={(e) => set("public_description", e.target.value)} placeholder="Beskriv bilen…" data-testid="desc-input"/>
          </Field>

          {/* Features */}
          <div>
            <Label>Udstyr</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {form.public_features.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 bg-zinc-800 text-zinc-200 text-xs px-3 py-1 rounded-full">
                  <Tag size={11} className="text-amber-500"/> {f}
                  <button onClick={() => removeFeature(i)} className="hover:text-red-400" data-testid={`feat-del-${i}`}><X size={11}/></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                className="ui-input flex-1"
                placeholder="Fx. Bluetooth, GPS, Læderindtræk…"
                value={form.featureInput}
                onChange={(e) => set("featureInput", e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(); } }}
                data-testid="feat-input"
              />
              <button onClick={addFeature} className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm" data-testid="feat-add">Tilføj</button>
            </div>
          </div>

          {car.status === "formidling" && (
            <div className="pt-4 mt-2 border-t border-zinc-800 flex items-center justify-between gap-3">
              <p className="text-xs text-zinc-500">
                Formidlings-biler kan slettes permanent uden at påvirke firmaets økonomi.
              </p>
              <DeleteFormidlingButton car={car} onDeleted={onSaved}/>
            </div>
          )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DeleteFormidlingButton({ car, onDeleted }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  async function doDelete() {
    setBusy(true);
    try {
      await axios.delete(`${API}/cars/${car.id}`, { headers: authHdr() });
      toast.success(`${car.license_plate} slettet`);
      onDeleted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setBusy(false); setConfirm(false); }
  }

  if (!confirm) {
    return (
      <button
        type="button"
        onClick={() => setConfirm(true)}
        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-300 hover:text-red-200 hover:bg-red-500/10 border border-red-500/40 rounded-lg transition-colors shrink-0"
        data-testid={`delete-formidling-${car.license_plate}`}
      >
        <Trash2 size={13}/> Slet bil
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 shrink-0" data-testid={`delete-confirm-${car.license_plate}`}>
      <span className="text-xs text-red-300">Er du sikker?</span>
      <button type="button" onClick={() => setConfirm(false)} className="px-3 py-2 text-xs text-zinc-400 hover:text-white" data-testid="delete-cancel">Annullér</button>
      <button type="button" onClick={doDelete} disabled={busy} className="px-3 py-2 text-xs font-medium bg-red-500 hover:bg-red-400 text-white rounded-lg disabled:opacity-50" data-testid="delete-confirm-btn">
        {busy ? "Sletter…" : "Slet permanent"}
      </button>
    </div>
  );
}

function SaveStatus({ status }) {
  if (status === "idle") return null;
  if (status === "saving") return (
    <span className="inline-flex items-center gap-1.5 text-xs text-zinc-400" data-testid="save-status-saving">
      <Loader2 size={12} className="animate-spin"/> Gemmer…
    </span>
  );
  if (status === "saved") return (
    <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400" data-testid="save-status-saved">
      <Check size={12}/> Gemt
    </span>
  );
  if (status === "error") return (
    <span className="inline-flex items-center gap-1.5 text-xs text-red-400" data-testid="save-status-error">
      Fejl
    </span>
  );
  return null;
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-3 cursor-pointer bg-zinc-800/50 hover:bg-zinc-800 px-4 py-2.5 rounded-lg border border-zinc-700" data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-amber-500"/>
      <span className="text-sm">{label}</span>
    </label>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      {children}
    </label>
  );
}

function Label({ children }) {
  return <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">{children}</div>;
}

function NumInput({ value, onChange, testid }) {
  return <input className="ui-input" type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}/>;
}

function numOrNull(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
}
function intOrNull(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseInt(v, 10);
  return isNaN(n) ? null : n;
}
